import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import SEOHead from '../../components/SEOHead';

interface Plan {
  id: string;
  name: string;
  price: number;
  priceIdMonthly: string;
  priceIdYearly: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
}

const defaultPlans: Plan[] = [
  {
    id: 'free',
    name: 'Gratuito',
    price: 0,
    priceIdMonthly: '',
    priceIdYearly: '',
    description: 'Para começar a organizar suas finanças',
    features: [
      'Até 20 transações/mês',
      '3 categorias de orçamento',
      'Dashboard básico',
      'Relatórios simples',
      'Suporte via WhatsApp'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29.9,
    priceIdMonthly: '',
    priceIdYearly: '',
    description: 'Para quem quer controle total',
    features: [
      '7 dias de teste grátis',
      'Transações ilimitadas',
      'Categorias ilimitadas',
      'Dashboard completo',
      'Relatórios avançados',
      'Metas financeiras',
      'Alertas inteligentes',
      'Exportar PDF/Excel',
      'Suporte prioritário'
    ],
    highlighted: true,
    badge: 'Mais Popular'
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 49.9,
    priceIdMonthly: '',
    priceIdYearly: '',
    description: 'Para profissionais exigentes',
    features: [
      '7 dias de teste grátis',
      'Tudo do plano Pro',
      'Assistente IA avançado',
      'Análise preditiva de gastos',
      'Planejamento financeiro personalizado',
      'Relatórios personalizados',
      'Suporte 24/7 via WhatsApp',
      'Acesso antecipado a novos recursos'
    ],
    badge: 'Completo'
  }
];

export default function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>(defaultPlans);
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCanceledModal, setShowCanceledModal] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Planos e Preços - MEUCONTROLE",
    "description": "Escolha o plano ideal para controlar suas finanças. Planos gratuito, Pro e Premium com recursos avançados de IA financeira. Teste grátis por 7 dias.",
    "url": `${siteUrl}/pricing`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "MEUCONTROLE",
      "url": siteUrl
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Product",
            "name": "MEUCONTROLE Gratuito",
            "description": "Para começar a organizar suas finanças pessoais",
            "brand": {
              "@type": "Brand",
              "name": "MEUCONTROLE"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "BRL",
              "availability": "https://schema.org/InStock",
              "url": `${siteUrl}/pricing`
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Product",
            "name": "MEUCONTROLE Pro",
            "description": "Para quem quer controle total das finanças com recursos avançados",
            "brand": {
              "@type": "Brand",
              "name": "MEUCONTROLE"
            },
            "offers": {
              "@type": "Offer",
              "price": "29.90",
              "priceCurrency": "BRL",
              "availability": "https://schema.org/InStock",
              "priceValidUntil": "2025-12-31",
              "url": `${siteUrl}/pricing`
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Product",
            "name": "MEUCONTROLE Premium",
            "description": "Para profissionais exigentes com IA avançada e análise preditiva",
            "brand": {
              "@type": "Brand",
              "name": "MEUCONTROLE"
            },
            "offers": {
              "@type": "Offer",
              "price": "49.90",
              "priceCurrency": "BRL",
              "availability": "https://schema.org/InStock",
              "priceValidUntil": "2025-12-31",
              "url": `${siteUrl}/pricing`
            }
          }
        }
      ]
    }
  };

  // Buscar Price IDs do Stripe
  useEffect(() => {
    const fetchStripePrices = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-stripe-prices`
        );
        const data = await response.json();
        
        if (data.success && data.prices) {
          setPlans(prevPlans => prevPlans.map(plan => {
            if (plan.id === 'pro') {
              return {
                ...plan,
                priceIdMonthly: data.prices.pro.monthly,
                priceIdYearly: data.prices.pro.yearly
              };
            }
            if (plan.id === 'premium') {
              return {
                ...plan,
                priceIdMonthly: data.prices.premium.monthly,
                priceIdYearly: data.prices.premium.yearly
              };
            }
            return plan;
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar preços do Stripe:', err);
      } finally {
        setLoadingPrices(false);
      }
    };

    fetchStripePrices();
  }, []);

  // Check URL params for success/canceled
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setShowSuccessModal(true);
      loadSubscription();
    }
    if (searchParams.get('canceled') === 'true') {
      setShowCanceledModal(true);
    }
  }, [searchParams]);

  // Load the current subscription when the user becomes available
  useEffect(() => {
    if (user) {
      loadSubscription();
    }
  }, [user]);

  const loadSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;
      if (data && data.plan) {
        setCurrentPlan(data.plan);
      }
    } catch (err) {
      console.error('Erro ao carregar assinatura:', err);
    }
  };

  const handleSelectPlan = async (plan: Plan) => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Free plan: just update the DB
    if (plan.id === 'free') {
      try {
        setLoadingPlan(plan.id);
        
        // Check if subscription exists
        const { data: existingSub } = await supabase
          .from('user_subscriptions')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (existingSub) {
          await supabase
            .from('user_subscriptions')
            .update({
              plan: 'free',
              status: 'active',
              updated_at: new Date().toISOString()
            })
            .eq('user_id', user.id);
        } else {
          await supabase
            .from('user_subscriptions')
            .insert({
              user_id: user.id,
              plan: 'free',
              status: 'active'
            });
        }

        setCurrentPlan('free');
        alert('Plano alterado para Gratuito!');
      } catch (err) {
        console.error('Erro ao alterar plano:', err);
        alert('Não foi possível mudar para o plano gratuito.');
      } finally {
        setLoadingPlan(null);
      }
      return;
    }

    // Verificar se os Price IDs estão carregados
    const priceId = billingCycle === 'monthly' ? plan.priceIdMonthly : plan.priceIdYearly;
    if (!priceId) {
      alert('Aguarde, carregando informações de pagamento...');
      return;
    }

    // Paid plans: redirect to Stripe checkout
    setLoadingPlan(plan.id);
    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('Token de acesso não encontrado. Faça login novamente.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            priceId: priceId,
            planName: plan.id,
            userId: user.id,
            userEmail: user.email,
            successUrl: `${window.location.origin}/pricing?success=true`,
            cancelUrl: `${window.location.origin}/pricing?canceled=true`
          })
        }
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error ?? 'Erro ao criar sessão de checkout');
      }
    } catch (err: any) {
      console.error('Erro ao iniciar checkout:', err);
      alert(err.message || 'Erro ao processar pagamento. Por favor, tente novamente.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const getYearlyPrice = (monthlyPrice: number) => {
    return (monthlyPrice * 12 * 0.8).toFixed(2);
  };

  return (
    <>
      <SEOHead
        title="Planos e Preços - MEUCONTROLE | Controle Financeiro com IA"
        description="Escolha o plano ideal para controlar suas finanças. Planos gratuito, Pro e Premium com recursos avançados de IA financeira. Teste grátis por 7 dias sem compromisso."
        keywords="planos meucontrole, preços controle financeiro, assinatura finanças, plano pro, plano premium, teste grátis financeiro"
        schema={schema}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50">
        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Assinatura Confirmada!</h3>
              <p className="text-gray-600 mb-6">
                Seu plano foi ativado com sucesso. Aproveite todos os recursos!
              </p>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/dashboard');
                }}
                className="w-full py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors cursor-pointer"
              >
                Ir para o Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Canceled Modal */}
        {showCanceledModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-information-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Pagamento Cancelado</h3>
              <p className="text-gray-600 mb-6">
                O processo de pagamento foi cancelado. Você pode tentar novamente quando quiser.
              </p>
              <button
                onClick={() => setShowCanceledModal(false)}
                className="w-full py-3 bg-gray-100 text-gray-800 rounded-xl font-semibold hover:bg-gray-200 transition-colors cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 cursor-pointer"
              >
                <h1
                  className="text-xl sm:text-2xl font-bold"
                  style={{ fontFamily: '"Pacifico", serif' }}
                >
                  MEUCONTROLE
                </h1>
              </button>

              <div className="flex items-center gap-2 sm:gap-4">
                {user ? (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-3 sm:px-4 py-2 text-teal-600 font-semibold hover:text-teal-700 cursor-pointer whitespace-nowrap text-sm"
                  >
                    <span className="hidden sm:inline">Voltar ao </span>Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 sm:px-6 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    Entrar
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8 sm:py-16">
          {/* Hero */}
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4">
              Escolha o plano ideal para você
            </h2>
            <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8">
              Comece grátis e evolua conforme suas necessidades
            </p>

            {/* Toggle Mensal/Anual */}
            <div className="inline-flex items-center gap-2 sm:gap-4 bg-white rounded-full p-1 shadow-lg">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 sm:px-6 py-2 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap text-sm ${
                  billingCycle === 'monthly' ? 'bg-teal-600 text-white' : 'text-gray-600'
                }`}
              >
                Mensal
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 sm:px-6 py-2 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap text-sm ${
                  billingCycle === 'yearly' ? 'bg-teal-600 text-white' : 'text-gray-600'
                }`}
              >
                Anual
                <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs bg-green-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full">
                  -20%
                </span>
              </button>
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 mb-10 sm:mb-16">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-lg border-2 transition-all hover:shadow-xl ${
                  plan.highlighted ? 'border-teal-600 md:scale-105' : 'border-gray-100'
                } ${currentPlan === plan.id ? 'ring-4 ring-teal-200' : ''}`}
              >
                {plan.badge && (
                  <div
                    className={`absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-bold ${
                      plan.highlighted ? 'bg-teal-600 text-white' : 'bg-gray-800 text-white'
                    }`}
                  >
                    {plan.badge}
                  </div>
                )}

                {currentPlan === plan.id && (
                  <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                    <span className="px-2 sm:px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-[10px] sm:text-xs font-bold">
                      Plano Atual
                    </span>
                  </div>
                )}

                <div className="text-center mb-4 sm:mb-6">
                  <h3 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">{plan.name}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">{plan.description}</p>
                  
                  {billingCycle === 'yearly' && plan.price > 0 ? (
                    <>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-lg sm:text-xl text-gray-400 line-through">
                          R$ {plan.price.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                          -20%
                        </span>
                      </div>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl sm:text-4xl font-bold text-green-600">
                          R$ {(plan.price * 0.8).toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-gray-500 text-sm">/mês</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl sm:text-4xl font-bold">
                        R$ {plan.price.toFixed(2).replace('.', ',')}
                      </span>
                      {plan.price > 0 && <span className="text-gray-500 text-sm">/mês</span>}
                    </div>
                  )}
                  
                  {billingCycle === 'yearly' && plan.price > 0 && (
                    <p className="text-xs sm:text-sm text-green-600 mt-1 font-medium">
                      R$ {getYearlyPrice(plan.price).replace('.', ',')} cobrado anualmente
                    </p>
                  )}
                </div>

                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 sm:gap-3">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-teal-600 text-xs sm:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-xs sm:text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={loadingPlan === plan.id || currentPlan === plan.id}
                  className={`w-full py-2.5 sm:py-3 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm ${
                    plan.highlighted
                      ? 'bg-teal-600 text-white hover:bg-teal-700'
                      : currentPlan === plan.id
                      ? 'bg-gray-100 text-gray-500'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {loadingPlan === plan.id ? (
                    <>
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      <span>Processando...</span>
                    </>
                  ) : currentPlan === plan.id ? (
                    'Plano Atual'
                  ) : plan.price === 0 ? (
                    'Começar Grátis'
                  ) : (
                    'Testar 7 Dias Grátis'
                  )}
                </button>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8">
              Perguntas Frequentes
            </h3>
            <div className="space-y-3 sm:space-y-4">
              {[
                {
                  q: 'Posso cancelar a qualquer momento?',
                  a: 'Sim! Você pode cancelar sua assinatura a qualquer momento. Seu acesso continuará até o final do período pago.'
                },
                {
                  q: 'Como funciona o período de teste?',
                  a: 'Todos os planos pagos incluem 7 dias de teste grátis. Você só será cobrado após esse período.'
                },
                {
                  q: 'Posso mudar de plano depois?',
                  a: 'Claro! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. A diferença será calculada proporcionalmente.'
                },
                {
                  q: 'Quais formas de pagamento são aceitas?',
                  a: 'Aceitamos cartões de crédito (Visa, Mastercard, Elo, American Express), PIX e boleto bancário.'
                }
              ].map((item, idx) => (
                <details key={idx} className="bg-white rounded-xl p-4 sm:p-6 shadow-lg cursor-pointer group">
                  <summary className="font-semibold flex items-center justify-between cursor-pointer text-sm sm:text-base">
                    {item.q}
                    <i className="ri-arrow-down-s-line text-xl transition-transform group-open:rotate-180"></i>
                  </summary>
                  <p className="mt-3 sm:mt-4 text-gray-600 text-xs sm:text-sm">{item.a}</p>
                </details>
              ))}
            </div>
          </div>

          {/* Guarantee */}
          <div className="mt-10 sm:mt-16 text-center">
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-green-50 border border-green-200 rounded-full px-4 sm:px-6 py-2 sm:py-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-green-500 rounded-full">
                <i className="ri-shield-check-line text-lg sm:text-xl text-white"></i>
              </div>
              <div className="text-left">
                <p className="font-bold text-green-800 text-sm sm:text-base">Garantia de 7 dias</p>
                <p className="text-xs sm:text-sm text-green-700">Não gostou? Devolvemos seu dinheiro</p>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 py-6 sm:py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-gray-600 text-xs sm:text-sm">
              © 2025 MeuControle. Todos os direitos reservados. |
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="nofollow"
                className="text-teal-600 hover:text-teal-700 ml-1"
              >
                Powered by Readdy
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
